import React, { useState, useEffect } from 'react';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { waybillsAPI } from '../../helpers/api';

interface Waybill {
  id: string;
  waybillNumber: string;
  status: string;
  supplierName: string | null;
  createdAt: string;
  scannedAt: string | null;
  site: {
    id: string;
    name: string;
  } | null;
  scannedBy: { firstName: string; lastName: string } | null;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { name: string };
}

interface ProjectWaybillsModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
  onUpdate?: () => void;
}

const statusStyles: Record<string, string> = {
  SCANNED: 'bg-info bg-opacity-10 text-info',
  PENDING: 'bg-warning bg-opacity-10 text-warning',
  MATCHED: 'bg-success bg-opacity-10 text-success',
  NOT_ARRIVED: 'bg-danger bg-opacity-10 text-danger',
  MANUAL: 'bg-secondary bg-opacity-10 text-secondary',
};

const statusLabels: Record<string, string> = {
  SCANNED: 'Okutuldu',
  PENDING: 'Bekliyor',
  MATCHED: 'Eşleşti',
  NOT_ARRIVED: 'Gelmedi',
  MANUAL: 'Manuel',
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ProjectWaybillsModal: React.FC<ProjectWaybillsModalProps> = ({
  isOpen,
  toggle,
  project,
  onUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [waybills, setWaybills] = useState<Waybill[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && project) {
      fetchData();
    }
  }, [isOpen, project]);

  const fetchData = async () => {
    if (!project) return;

    try {
      setLoading(true);
      const response = await waybillsAPI.getAll({ projectId: project.id });
      setWaybills(response.data);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İrsaliyeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!project) return null;

  const headerActions = (
    <button
      type="button"
      className="side-modal-expand-btn"
      onClick={toggleExpand}
      title={isExpanded ? 'Küçült' : 'Genişlet'}
    >
      <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
    </button>
  );

  // Count by status
  const statusCounts = waybills.reduce((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Group by site
  const waybillsBySite = waybills.reduce((acc, w) => {
    const siteName = w.site?.name || 'Bilinmeyen';
    if (!acc[siteName]) acc[siteName] = [];
    acc[siteName].push(w);
    return acc;
  }, {} as Record<string, Waybill[]>);

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Proje İrsaliyeleri"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <h4 className="mb-1" style={{ fontWeight: 600 }}>{project.name}</h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {project.city.name} • {project.code}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <>
            {/* Status Summary */}
            {waybills.length > 0 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Durum Özeti
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <div
                      key={status}
                      className={`px-3 py-2 rounded-3 ${statusStyles[status]}`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <span style={{ fontWeight: 500 }}>{count}</span>
                      <span className="ms-1">{statusLabels[status]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Site Summary */}
            {waybills.length > 0 && Object.keys(waybillsBySite).length > 1 && (
              <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }} className="rounded-3 p-3 mb-3">
                <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Şantiye Bazında
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(waybillsBySite).map(([siteName, wList]) => (
                    <div key={siteName} className="px-3 py-2 rounded-3" style={{ fontSize: '0.85rem', background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
                      <i className="ri-building-line text-primary me-1"></i>
                      <span style={{ fontWeight: 500 }}>{wList.length}</span>
                      <span className="ms-1">{siteName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Waybills List */}
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  İrsaliye Listesi
                </span>
                <span className="text-muted small">{waybills.length} irsaliye</span>
              </div>
              {waybills.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-0">Bu projede henüz irsaliye bulunmuyor</p>
                </div>
              ) : (
                <div style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }} className="rounded-3 overflow-hidden">
                  {waybills.slice(0, 30).map((waybill, idx) => (
                    <div
                      key={waybill.id}
                      className="px-3 py-3"
                      style={{ borderBottom: idx < Math.min(waybills.length, 30) - 1 ? '1px solid #e9ecef' : 'none' }}
                    >
                      <div className="d-flex align-items-start justify-content-between">
                        <div style={{ flex: 1 }}>
                          <div className="d-flex align-items-center mb-1">
                            <span style={{ fontWeight: 500 }}>{waybill.waybillNumber}</span>
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                            {waybill.site && (
                              <span className="me-3">
                                <i className="ri-building-line me-1"></i>
                                {waybill.site.name}
                              </span>
                            )}
                            {waybill.supplierName && (
                              <span className="me-3">
                                <i className="ri-store-2-line me-1"></i>
                                {waybill.supplierName}
                              </span>
                            )}
                            <span>
                              <i className="ri-time-line me-1"></i>
                              {formatDate(waybill.scannedAt || waybill.createdAt)}
                            </span>
                          </div>
                        </div>
                        <span
                          className={`px-2 py-1 rounded-pill small ${statusStyles[waybill.status]}`}
                          style={{ fontWeight: 500, fontSize: '0.75rem' }}
                        >
                          {statusLabels[waybill.status]}
                        </span>
                      </div>
                    </div>
                  ))}
                  {waybills.length > 30 && (
                    <div className="px-3 py-2 text-center text-muted" style={{ fontSize: '0.85rem' }}>
                      +{waybills.length - 30} irsaliye daha
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default ProjectWaybillsModal;
